<template>
  <div class="container-fluid d-flex justify-content-center mt-5 pt-5 p-lg-4">
    <div class="row container mt-5 pt-3">
      <div class="col-12">
        <h2 class="mb-4">
          <i class="fas fa-leaf me-2 text-primary"></i>
          {{ isEditMode ? 'Editar Producto' : 'Agregar Producto' }}
        </h2>

        <form @submit.prevent="handleSubmit" class="needs-validation">
          <!-- Información Básica -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 fs-6 py-3">
                <i class="fas fa-info-circle me-2"></i>Información Básica
              </h4>
            </div>
            <div class="card-body">
              <div class="row g-3">                <div class="col-md-6 position-relative">
                  <label class="form-label">
                    Nombre del banco
                    <span v-if="selectedBrand" class="badge bg-success ms-2">
                      <i class="fas fa-check me-1"></i>Vinculado
                    </span>
                  </label>
                  <div class="input-group">
                    <input 
                      type="text" 
                      class="form-control" 
                      :class="{ 'is-valid': selectedBrand }"
                      placeholder="Ejemplo: BSF Seeds" 
                      required
                      :value="brandSearchText || product.brand"
                      @input="handleBrandInput"
                      @focus="handleBrandFocus"
                      @blur="handleBrandBlur"
                    />
                    <button 
                      v-if="selectedBrand || brandSearchText" 
                      type="button" 
                      class="btn btn-outline-secondary" 
                      @click="clearBrandSelection"
                      title="Limpiar selección"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <!-- Información de la marca seleccionada -->
                  <div v-if="selectedBrand" class="mt-2">
                    <small class="text-success">
                      <i class="fas fa-info-circle me-1"></i>
                      Marca existente: <strong>{{ selectedBrand.brand_name }}</strong> (ID: {{ selectedBrand.brand_id }})
                    </small>
                  </div>
                  <!-- Dropdown de marcas filtradas -->
                  <div v-if="showBrandDropdown" class="brand-dropdown">
                    <!-- Mostrar marcas existentes -->
                    <div v-if="filteredBrands.length > 0" class="brand-list">
                      <div class="px-3 py-2 text-muted small border-bottom">
                        Marcas existentes ({{ filteredBrands.length }})
                      </div>
                      <div
                        v-for="brand in filteredBrands"
                        :key="brand.brand_id || brand.id"
                        class="brand-item w-100 d-flex align-items-center justify-content-start"
                        :class="{ 'selected': selectedBrand && selectedBrand.id === brand.id }"
                        @mousedown="selectBrand(brand)"
                      >
                        <div class="d-flex align-items-center justify-content-start">
                          <div v-if="brand.image" class="brand-image me-2">
                            <img :src="brand.image" alt="Logo" height="20" />
                          </div>
                          <div class="flex-grow-1">
                            <div class="fw-semibold">{{ brand.brand_name }}</div>
                            <div class="text-muted small">ID: {{ brand.brand_id }}</div>
                          </div>
                          <div v-if="selectedBrand && selectedBrand.id === brand.id" class="text-success">
                            <i class="fas fa-check"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Opción para crear nueva marca -->
                    <div v-if="brandSearchText && brandSearchText.trim() && !filteredBrands.some(b => b.brand_name.toLowerCase() === brandSearchText.toLowerCase())" class="create-brand-option border-top">
                      <div class="px-3 py-2 text-muted small">
                        Nueva marca
                      </div>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary w-100 mx-2 mb-2" 
                        @mousedown="createNewBrand"
                        :disabled="isCreatingNewBrand"
                      >
                        <i class="fas fa-plus me-1"></i>
                        {{ isCreatingNewBrand ? 'Creando...' : `Crear marca "${brandSearchText.trim()}"` }}
                      </button>
                    </div>
                    
                    <!-- Mensaje cuando no hay resultados -->
                    <div v-if="filteredBrands.length === 0 && (!brandSearchText || !brandSearchText.trim())" class="px-3 py-2 text-muted text-center">
                      Escribe para buscar marcas o crear una nueva
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Nombre del Producto</label>
                  <input v-model="product.name" type="text" class="form-control" placeholder="Ejemplo: Gorilla Glue" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Genotipo</label>
                  <input v-model="product.genotype" type="text" class="form-control" placeholder="Ejemplo: Gorilla glue auto x Orange Blossom" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Sabores</label>
                  <input v-model="product.flavors" type="text" class="form-control" placeholder="Ejemplo: Cítrico, Terrozo" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Efectos</label>
                  <input v-model="product.effects" type="text" class="form-control" placeholder="Ejemplo: Eufórico, Narcótico" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Satividad (%)</label>
                  <input v-model="product.sativa" type="text" class="form-control" placeholder="Ejemplo: 40%" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Indica (%)</label>
                  <input v-model="product.indica" type="text" class="form-control" placeholder="Ejemplo: 60%" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Tipo</label>
                  <select v-model="product.type" class="form-select">
                    <option value="fem">Feminizada</option>
                    <option value="auto">Autofloreciente</option>
                    <option value="reg">Regular</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Contenido de THC (%)</label>
                  <input v-model="product.thc" type="text" class="form-control" placeholder="Ejemplo: 20%" />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Contenido de CBD (%)</label>
                  <input v-model="product.cbd" type="text" class="form-control" placeholder="Ejemplo: 1%" />
                </div>
                <div class="col-12">
                  <label class="form-label">Descripción</label>
                  <textarea v-model="product.description" class="form-control" rows="3" placeholder="Descripción del producto"></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-image me-2"></i>Imágenes
              </h4>
              <small class="text-muted fs-6">
                <i class="fas fa-info-circle me-1"></i>
                Resolución recomendada: 1000x1000px
              </small>
            </div>
            <div class="card-body d-flex row" @paste="handlePaste">
              <div class="d-flex">
                <!-- Vista Previa de Imagen -->
                <div v-if="imagePreview" class="col-md-6 d-flex align-items-end">
                 <img :src="imagePreview" class="img-preview rounded" alt="Vista previa del producto" />
                 <button type="button" class="btn btn-danger ms-4" @click="removeImage('image')">
                   <i class="fas fa-trash"></i>
                 </button>
               </div>
               <div v-if="imagePreview2" class="col-md-6 d-flex align-items-end">
                 <img :src="imagePreview2" class="img-preview rounded" alt="Vista previa del producto" />
                 <button type="button" class="btn btn-danger ms-4" @click="removeImage('image_2')">
                   <i class="fas fa-trash"></i>
                 </button>
               </div>
              </div>
              <div class="col-md-6">
                <label class="form-label pt-3">Imagen principal</label>
                <input type="file" class="form-control" @change="handleUploadImage($event, 'image')" ref="imageUrlInput" />
              </div>
              <div class="col-md-6 ps-0">
                <label class="form-label pt-3">Imagen 2</label>
                <input type="file" class="form-control" @change="handleUploadImage($event, 'image_2')" ref="imageUrlInput2" />
              </div>
            </div>
          </div>

          <!-- Información de Crecimiento -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-seedling me-2"></i>Información de Crecimiento
              </h4>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Tiempo de Floración</label>
                  <input v-model="product.flowering_time" type="text" class="form-control" placeholder="Ejemplo: 70 Días / 7 Semanas" />
                </div>
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Ciclo Completo</label>
                  <input v-model="product.completed_cicle" type="text" class="form-control" placeholder="Ejemplo: 70 Días / 7 Semanas" />
                </div>
                <!-- Información de Altura -->
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Altura Interior</label>
                  <input v-model="product.height_indoor" type="text" class="form-control" placeholder="Ejemplo: 100-150 cm" />
                </div>
                <div class="col-lg-6 col-6">
                  <label class="form-label">Altura Exterior</label>
                  <input v-model="product.height_outdoor" type="text" class="form-control" placeholder="Ejemplo: 150-200 cm" />
                </div>

                <!-- Información de Rendimiento -->
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Rendimiento Interior</label>
                  <input v-model="product.yield_indoor" type="text" class="form-control" placeholder="Ejemplo: 500 g/m²" />
                </div>
                <div class="col-lg-6 col-6 ">
                  <label class="form-label">Rendimiento Exterior</label>
                  <input v-model="product.yield_outdoor" type="text" class="form-control" placeholder="Ejemplo: 600 g/planta" />
                </div>
              </div>
            </div>
          </div>

          <!-- Información de Inventario -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-box me-2"></i>Presentaciones
              </h4>
              <button type="button" class="btn btn-outline-primary btn-sm" @click="addBlister">
                <i class="fas fa-plus me-2"></i>Agregar Presentación
              </button>
            </div>
            <div class="card-body">
              <div v-if="!product.blisters.length" class="text-center text-muted py-4">
                No hay presentaciones agregadas
              </div>
              <div v-else class="blisters-list">
                <div v-for="(blister, index) in product.blisters" :key="index" class="blister-item mb-3">
                  <div class="row g-3 align-items-end">
                    <div class="col-md-2">
                      <label class="form-label">Semillas</label>
                      <input 
                        v-model="blister.seed_count" 
                        type="number" 
                        class="form-control" 
                        placeholder="Cantidad"
                        required
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Precio</label>
                      <input 
                        v-model="blister.price" 
                        type="number" 
                        class="form-control" 
                        placeholder="$"
                        required
                      />
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Stock</label>
                      <input 
                        v-model="blister.stock" 
                        type="number" 
                        class="form-control" 
                        placeholder="Disponible"
                        required
                      />
                    </div>
                    <!-- Nuevo campo de descuento -->
                    <div class="col-md-2">
                      <label class="form-label">Descuento (%)</label>
                      <input 
                        v-model="blister.discount"
                        type="number" 
                        class="form-control" 
                        placeholder="0"
                        min="0"
                        max="100"
                        @input="validateDiscount($event, index)"
                      />
                    </div>
                    <!-- Mostrar precio con descuento -->
                    <div class="col-md-2" v-if="blister.discount > 0">
                      <label class="form-label text-success">Precio final</label>
                      <div class="price-preview text-success">
                        ${{ formatDiscountedPrice(blister) }}
                      </div>
                    </div>
                    <div class="col-md-1 d-flex align-items-center justify-content-end">
                      <button 
                        type="button" 
                        class="btn btn-outline-danger" 
                        @click="removeBlister(index)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Metadatos -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5"><i class="fas fa-tags me-2"></i>Metadatos</h4>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12">
                  <label class="form-label">SKU</label>
                  <input v-model="product.sku" type="text" class="form-control" placeholder="Ejemplo: GG-001" />
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <i class="fas fa-save me-2"></i>
              {{ isSubmitting ? "Guardando..." : (isEditMode ? "Actualizar Producto" : "Guardar Producto") }}
            </button>
            <button type="button" class="btn btn-secondary" @click="resetForm">
              <i class="fas fa-undo me-2"></i>Reiniciar
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getProduct } from "@/services/products";
import { createProduct, updateProduct, uploadImage } from "@/services/products";
import { getBrands, createBrand } from "@/services/brands";
import { normalizeBrandName } from "@/utils/brandUtils";
// toastification
import { useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";

// Configuración de toastification
const toast = useToast();

// Estructura inicial del producto
const initialProduct = {
  brand_id: "",
  brand: "",
  name: "",
  type: "fem",
  genotype: "",
  sativa: "",
  indica: "",
  description: "",
  flowering_time: "",
  completed_cicle: "",
  height_indoor: "",
  height_outdoor: "",
  yield_indoor: "",
  yield_outdoor: "",
  thc: "",
  cbd: "",
  seed_count: 0,
  price: 0,
  stock: 0,
  sku: "",
  image: null,
  image_2: null,
  image_3: null,
  blisters: []
};

// Variables para el manejo de marcas
const brands = ref([]);
const filteredBrands = ref([]);
const showBrandDropdown = ref(false);
const isCreatingNewBrand = ref(false);
const brandSearchText = ref("");
const selectedBrand = ref(null);

const product = ref({ ...initialProduct });
const imagePreview = ref(null);
const imagePreview2 = ref(null);
const imagePreview3 = ref(null);
const imageUrlInput = ref(null);
const imageUrlInput2 = ref(null);
const imageUrlInput3 = ref(null);
const isSubmitting = ref(false);
const isEditMode = ref(false);
const route = useRoute();
const router = useRouter();
const productId = route.params.productId;

onMounted(async () => {  
  try {
    console.log('ProductForm mounted, productId:', productId);
    
    // Cargar todas las marcas disponibles
    console.log('Loading brands...');
    brands.value = await getBrands();
    console.log('Brands loaded:', brands.value.length);
    
    if (productId) {
      console.log('Edit mode - loading product with ID:', productId);
      isEditMode.value = true;
      
      try {
        const productData = await getProduct(productId);
        console.log('Product data loaded:', productData);
        
        // Asegurarse de que brand_id existe y es correcto
        if (productData.brand && !productData.brand_id) {
          productData.brand_id = productData.brand.toLowerCase().replace(/\s+/g, '-');
        }
        
        product.value = { ...productData };
        imagePreview.value = productData.image;
        imagePreview2.value = productData.image_2;
        imagePreview3.value = productData.image_3;
        
        // Configurar el texto de búsqueda y la marca seleccionada
        if (productData.brand) {
          brandSearchText.value = productData.brand;
          
          // Buscar la marca en la lista usando múltiples criterios
          selectedBrand.value = brands.value.find(b => 
            b.brand_name === productData.brand || 
            b.brand_id === productData.brand_id ||
            b.brand_name.toLowerCase() === productData.brand.toLowerCase()
          ) || null;
          
          console.log('Selected brand found:', selectedBrand.value);
        }
        
        // Inicializar filteredBrands para mostrar marcas relacionadas al editar
        if (productData.brand) {
          const query = productData.brand.toLowerCase().trim();
          filteredBrands.value = brands.value.filter(brand => 
            brand.brand_name.toLowerCase().includes(query) ||
            brand.brand_name.toLowerCase().startsWith(query.substring(0, 3))
          );
        }
        
      } catch (productError) {
        console.error('Error loading specific product:', productError);
        toast.error(`Error al cargar el producto: ${productError.message}`);
        // Redirigir a la lista de productos si no se puede cargar el producto
        router.push({ name: 'AdminProductList' });
        return;
      }
    } else {
      console.log('Create mode - no productId provided');
    }
  } catch (error) {
    console.error("Error al cargar datos iniciales:", error);
    toast.error(`Error al cargar los datos iniciales: ${error.message}`);
  }
});

const handleUploadImage = async (event, imageField) => {
  const file = event.target.files[0];
  try {
    const imageUrl = await uploadImage(file);
    product.value[imageField] = imageUrl;
    
    if (imageField === 'image') {
      imagePreview.value = imageUrl;
    } else if (imageField === 'image_2') {
      imagePreview2.value = imageUrl;
    } else if (imageField === 'image_3') {
      imagePreview3.value = imageUrl;
    }
  } catch (error) {
    console.error('Error uploading image:', error);
    toast.error('Error al subir la imagen');
  }
};

const handlePaste = async (event) => {
  const items = event.clipboardData.items;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      await handleUploadImage({ target: { files: [file] } }, 'image');
    }
  }
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    // Validar que se haya seleccionado o ingresado una marca
    if (!product.value.brand || !product.value.brand.trim()) {
      toast.error("Por favor, selecciona o ingresa una marca");
      return;
    }
    
    // Asegurarse de que el brand_id está correctamente formateado
    let brand_id = product.value.brand_id;
    if (!brand_id) {
      brand_id = product.value.brand.toLowerCase().replace(/\s+/g, '-');
    }
    
    const productToSend = {
      ...product.value,
      brand: product.value.brand.trim(), // Limpiar espacios en blanco
      brand_id: brand_id,
      blisters: product.value.blisters.map(blister => ({
        ...blister,
        seed_count: Number(blister.seed_count),
        price: Number(blister.price),
        stock: Number(blister.stock),
        discount: Number(blister.discount || 0)
      }))
    };

    console.log('Submitting product:', productToSend);

    if (isEditMode.value) {
      await updateProduct(productId, productToSend);
      toast.success("Producto actualizado");
    } else {
      await createProduct(productToSend);
      toast.success("Producto guardado");
      router.push({ name: 'AdminProductList' }); // Solo redirige en caso de crear nuevo
    }
    localStorage.removeItem('products');
    localStorage.removeItem('admin_products'); // También limpiar caché de admin
  } catch (error) {
    console.error(error);
    toast.error(error.message);
  } finally {
    isSubmitting.value = false;
  }
};

const removeImage = (imageField) => {
  product.value[imageField] = null;
  if (imageField === 'image') {
    imagePreview.value = null;
    if (imageUrlInput.value) {
      imageUrlInput.value.value = "";
    }
  } else if (imageField === 'image_2') {
    imagePreview2.value = null;
    if (imageUrlInput2.value) {
      imageUrlInput2.value.value = "";
    }
  } else if (imageField === 'image_3') {
    imagePreview3.value = null;
    if (imageUrlInput3.value) {
      imageUrlInput3.value.value = "";
    }
  }
};

const resetForm = () => {
  product.value = { 
    ...initialProduct,
    blisters: []
  };
  // Reiniciar imágenes
  imagePreview.value = null;
  imagePreview2.value = null;
  imagePreview3.value = null;
  if (imageUrlInput.value) {
    imageUrlInput.value.value = "";
  }
  if (imageUrlInput2.value) {
    imageUrlInput2.value.value = "";
  }
  if (imageUrlInput3.value) {
    imageUrlInput3.value.value = "";
  }
  
  // Reiniciar variables relacionadas con la marca
  brandSearchText.value = "";
  selectedBrand.value = null;
  showBrandDropdown.value = false;
  filteredBrands.value = [];
};

// Función auxiliar para limpiar la marca seleccionada
const clearBrandSelection = () => {
  selectedBrand.value = null;
  brandSearchText.value = "";
  product.value.brand = "";
  product.value.brand_id = "";
  showBrandDropdown.value = false;
  filteredBrands.value = [];
};

const addBlister = () => {
  product.value.blisters.push({
    seed_count: 0,
    price: 0,
    stock: 0,
    discount: 0 // Agregar campo de descuento
  });
};

const validateDiscount = (event, index) => {
  let value = parseInt(event.target.value);
  if (isNaN(value)) value = 0;
  if (value < 0) value = 0;
  if (value > 100) value = 100;
  product.value.blisters[index].discount = value;
};

const formatDiscountedPrice = (blister) => {
  if (!blister.discount) return blister.price;
  const discount = blister.discount / 100;
  const finalPrice = blister.price * (1 - discount);
  return finalPrice.toFixed(2);
};

const removeBlister = (index) => {
  product.value.blisters.splice(index, 1);
};

const handleBrandInput = (e) => {
  const query = e.target.value.toLowerCase().trim();
  brandSearchText.value = e.target.value;
  product.value.brand = e.target.value;
  
  // Actualizamos brand_id cuando se modifica la marca (usando normalización)
  if (product.value.brand) {
    const normalized = normalizeBrandName(product.value.brand);
    product.value.brand = normalized;
    product.value.brand_id = normalized.toLowerCase().replace(/\s+/g, '-');
  }
  
  // Limpiar la marca seleccionada si el texto cambia
  if (selectedBrand.value && normalizeBrandName(selectedBrand.value.brand_name) !== normalizeBrandName(e.target.value)) {
    selectedBrand.value = null;
  }
  
  // Filtramos las marcas basados en el texto ingresado con mejor lógica y normalización
  if (query) {
    filteredBrands.value = brands.value.filter(brand => {
      const brandNameLower = normalizeBrandName(brand.brand_name).toLowerCase();
      const normalizedQuery = normalizeBrandName(query).toLowerCase();
      return (
        brandNameLower.includes(normalizedQuery) ||
        brandNameLower.startsWith(normalizedQuery) ||
        // También buscar por brand_id
        (brand.brand_id && brand.brand_id.toLowerCase().includes(query)) ||
        // Búsqueda por coincidencias parciales en palabras
        brand.brand_name.toLowerCase().split(' ').some(word => 
          word.startsWith(normalizedQuery)
        )
      );
    }).sort((a, b) => {
      // Priorizar coincidencias exactas al inicio
      const aName = normalizeBrandName(a.brand_name).toLowerCase();
      const bName = normalizeBrandName(b.brand_name).toLowerCase();
      const normalizedQuery = normalizeBrandName(query).toLowerCase();
      
      if (aName.startsWith(normalizedQuery) && !bName.startsWith(normalizedQuery)) return -1;
      if (bName.startsWith(normalizedQuery) && !aName.startsWith(normalizedQuery)) return 1;
      return aName.localeCompare(bName);
    }); // Quitar límite - mostrar todas las marcas filtradas
    
    showBrandDropdown.value = true;
  } else {
    // Si no hay texto, mostrar todas las marcas ordenadas alfabéticamente
    filteredBrands.value = brands.value
      .sort((a, b) => a.brand_name.localeCompare(b.brand_name)); // Mostrar TODAS las marcas sin límite
    showBrandDropdown.value = true;
  }
};

const selectBrand = (brand) => {
  selectedBrand.value = brand;
  
  // Actualizamos los valores del producto
  product.value.brand = brand.brand_name;
  product.value.brand_id = brand.brand_id;
  
  // También actualizamos el texto de búsqueda para que se muestre en el input
  brandSearchText.value = brand.brand_name;
  
  // Si el brand tiene una imagen, podríamos usarla
  if (brand.image) {
    // Opcionalmente, podríamos hacer algo con la imagen de la marca
    // Por ejemplo, mostrarla en algún lugar
  }
  
  showBrandDropdown.value = false;
  toast.success(`Marca "${brand.brand_name}" seleccionada`);
};

const createNewBrand = async () => {
  // Usamos el brandSearchText o el valor actual del campo brand
  let brandName = brandSearchText.value || product.value.brand;
  
  if (!brandName.trim()) {
    toast.error("Por favor, ingrese un nombre para la marca");
    return;
  }

  // Normalizar el nombre de la marca
  brandName = normalizeBrandName(brandName.trim());

  // Verificar si la marca ya existe (comparación normalizada)
  const existingBrand = brands.value.find(b => 
    normalizeBrandName(b.brand_name) === brandName
  );
  
  if (existingBrand) {
    toast.warning(`La marca "${brandName}" ya existe. Seleccionándola...`);
    selectBrand(existingBrand);
    return;
  }

  isCreatingNewBrand.value = true;
  try {
    const formattedBrandId = brandName.toLowerCase().replace(/\s+/g, '-');
    
    const newBrandData = {
      brand_name: brandName,
      brand_id: formattedBrandId,
      description: "Marca creada desde el formulario de productos",
      visible: true
    };
    
    console.log('Creating new brand:', newBrandData);
    const createdBrand = await createBrand(newBrandData);
    console.log('Brand created successfully:', createdBrand);
    
    // Añadimos la nueva marca a la lista y la seleccionamos
    brands.value.unshift(createdBrand); // Agregar al inicio para que aparezca primero
    
    // Actualizamos la marca en el producto
    product.value.brand = createdBrand.brand_name;
    product.value.brand_id = createdBrand.brand_id;
    brandSearchText.value = createdBrand.brand_name;
    selectedBrand.value = createdBrand;
    
    showBrandDropdown.value = false;
    toast.success(`Marca "${createdBrand.brand_name}" creada correctamente`);
  } catch (error) {
    console.error("Error al crear la marca:", error);
    toast.error("Error al crear la marca: " + (error.message || "Error desconocido"));
  } finally {
    isCreatingNewBrand.value = false;
  }
};

const handleBrandBlur = () => {
  // Damos un pequeño retraso antes de ocultar el dropdown para permitir que se haga clic en opciones
  setTimeout(() => {
    showBrandDropdown.value = false;
  }, 200);
};

const handleBrandFocus = () => {
  const searchValue = brandSearchText.value || product.value.brand;
  
  // Siempre mostramos el dropdown al enfocar
  if (searchValue && searchValue.trim()) {
    // Si hay texto, filtramos con mejor lógica
    const query = searchValue.toLowerCase().trim();
    filteredBrands.value = brands.value.filter(brand => {
      const brandNameLower = brand.brand_name.toLowerCase();
      return (
        brandNameLower.includes(query) ||
        brandNameLower.startsWith(query) ||
        (brand.brand_id && brand.brand_id.toLowerCase().includes(query)) ||
        // Búsqueda por coincidencias parciales en palabras
        brand.brand_name.toLowerCase().split(' ').some(word => 
          word.startsWith(query)
        )
      );
    }).sort((a, b) => {
      // Priorizar coincidencias exactas al inicio
      const aName = a.brand_name.toLowerCase();
      const bName = b.brand_name.toLowerCase();
      
      if (aName.startsWith(query) && !bName.startsWith(query)) return -1;
      if (bName.startsWith(query) && !aName.startsWith(query)) return 1;
      return aName.localeCompare(bName);
    }); // Mostrar TODAS las marcas que coincidan
  } else {
    // Si no hay texto, mostramos TODAS las marcas ordenadas alfabéticamente
    filteredBrands.value = brands.value
      .sort((a, b) => a.brand_name.localeCompare(b.brand_name)); // Mostrar TODAS las marcas
  }
  
  showBrandDropdown.value = true;
};
</script>

<style scoped>
.card {
  border: none;
  border-radius: 8px;
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

.card-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

input, textarea,
.form-select {
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

select {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

option {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}


.img-preview {
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
}

.blister-item {
  background: var(--black-color-2);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.blister-item:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

.blisters-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.blisters-list::-webkit-scrollbar {
  width: 8px;
}

.blisters-list::-webkit-scrollbar-track {
  background: var(--black-color-2);
  border-radius: 4px;
}

.blisters-list::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: 4px;
}

.price-preview {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  background: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
}

.card-header small {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

/* Estilos para el dropdown de marcas */
.position-relative {
  position: relative;
}

.brand-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: var(--black-color-2);
  border-radius: 0 0 8px 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none;
  z-index: 1050;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-height: 400px; /* Aumentar altura máxima para mostrar más marcas */
  overflow-y: auto;
}

.brand-list {
  max-height: 350px; /* Aumentar altura máxima de la lista */
  overflow-y: auto;
}

.brand-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.brand-item:hover {
  background-color: var(--primary-color);
  color: #fff;
}

.brand-item.selected {
  background-color: rgba(var(--primary-color-rgb), 0.2);
  border-left: 3px solid var(--primary-color);
}

.brand-item:last-child {
  border-bottom: none;
}

.brand-image {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.1);
}

.brand-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.create-brand-option {
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.02);
}

.z-index-dropdown {
  z-index: 1050;
}

/* Input group styling for brand selection */
.input-group .form-control.is-valid {
  border-color: #198754;
  background-image: none;
}

.input-group .form-control.is-valid:focus {
  border-color: #198754;
  box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25);
}

.badge.bg-success {
  font-size: 0.7em;
  padding: 0.25rem 0.5rem;
}

/* Mejorar scrollbar del dropdown */
.brand-dropdown::-webkit-scrollbar {
  width: 6px;
}

.brand-dropdown::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.brand-dropdown::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: 3px;
}

.brand-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--primary-color-rgb), 0.8);
}
</style>